/**
 * PRESET CONTRACT — the shape every colour scheme must satisfy.
 * ============================================================
 *
 * A preset is Tier 1 of the token system: raw colour values with no meaning
 * attached. `tokens/semantic.js` maps roles onto these SLOTS, never onto a
 * named palette — which is what makes swapping a scheme a one-line change in
 * `presets/index.js` instead of a rewrite of every role.
 *
 * ── Why the slot names look the way they do ─────────────────────────────────
 *
 * `surfaceScale` is a 10-step ramp keyed 900→50, darkest first. It exists as a
 * RAMP rather than five named surfaces because the ramp is also published to
 * Chakra as the `navy` colour scale, and ~1,000 `useColorModeValue('x','navy.800')`
 * call-sites across the app resolve against it. Renaming or flattening it would
 * silently unstyle those call-sites. Keep the ramp; the named surfaces below are
 * derived from it.
 *
 * ── Adding a new scheme ─────────────────────────────────────────────────────
 *
 *   1. Copy an existing preset, change the values, keep every key.
 *   2. `node scripts/theme/contrast-check.js` — must pass AA.
 *   3. Set ACTIVE in `presets/index.js`.
 *
 * Nothing in `views/` should ever need to change.
 */

/** Every key a preset must define, by path. Used by validatePreset(). */
export const REQUIRED = {
	'surfaceScale': [900, 850, 800, 700, 600, 500, 400, 300, 200, 100, 50],
	'paper': [0, 50, 100, 200, 300, 400, 500],
	'ink': ['dPrimary', 'dSecondary', 'dMuted', 'lPrimary', 'lSecondary', 'lMuted'],
	// `inkLight` is the accent darkened for use AS TEXT on light surfaces —
	// the vivid primary fails AA on white, and light mode reads the accent
	// through this slot (text.accent, border.focus, focus.ring).
	'accent': ['glow', 'light', 'primary', 'dark', 'hover', 'onAccent', 'inkLight', 'gradientEnd'],
	// The accent as a Chakra colour scale — see the note in navy.js. ~550
	// call-sites reach the accent through this, not through semantic tokens.
	'accentScale': [50, 100, 200, 300, 400, 500, 600, 700, 800, 900],
	'feedback': ['successD', 'successL', 'warningD', 'warningL', 'dangerD', 'dangerL', 'infoD', 'infoL'],
};

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Validate a preset against the contract.
 *
 * Returns an array of problem strings — empty means valid. Deliberately returns
 * rather than throws so the contrast script can report every fault at once
 * instead of stopping at the first.
 */
export function validatePreset(preset) {
	const problems = [];
	if (!preset || typeof preset !== 'object') return ['preset is not an object'];
	if (!preset.name) problems.push('missing `name`');

	Object.entries(REQUIRED).forEach(([group, keys]) => {
		const bag = preset[group];
		if (!bag || typeof bag !== 'object') {
			problems.push(`missing group \`${group}\``);
			return;
		}
		keys.forEach((k) => {
			const v = bag[k];
			if (v === undefined) {
				problems.push(`${group}.${k} is missing`);
			} else if (typeof v !== 'string' || !HEX.test(v)) {
				// Every slot must be a plain 6-digit hex: these values are handed to
				// SVG fills, alpha() maths and Chakra scales, none of which accept a
				// token name or a shorthand.
				problems.push(`${group}.${k} must be a #RRGGBB hex, got ${JSON.stringify(v)}`);
			}
		});
	});

	// The surface ramp must actually get lighter as the number falls, or the
	// elevation model (base → card → elevated) inverts and every panel reads
	// as a hole instead of a raised surface.
	const ramp = preset.surfaceScale;
	if (ramp && typeof ramp === 'object') {
		const lum = (hex) => {
			if (!HEX.test(String(hex))) return null;
			const n = parseInt(String(hex).slice(1), 16);
			return 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
		};
		const steps = [900, 850, 800, 700, 600, 500];
		for (let i = 1; i < steps.length; i += 1) {
			const prev = lum(ramp[steps[i - 1]]);
			const cur = lum(ramp[steps[i]]);
			if (prev != null && cur != null && cur < prev) {
				problems.push(
					`surfaceScale.${steps[i]} (${ramp[steps[i]]}) is darker than ` +
						`surfaceScale.${steps[i - 1]} (${ramp[steps[i - 1]]}) — the elevation ramp must lighten`,
				);
			}
		}
	}

	return problems;
}

export default { REQUIRED, validatePreset };
